import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { BrowserUtils } from '@azure/msal-browser'
import { MatDialog } from '@angular/material/dialog'
import { MatIconRegistry } from '@angular/material/icon'
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav'
import { DomSanitizer } from '@angular/platform-browser'
import { Router, RouterOutlet } from '@angular/router'
import { select, Store } from '@ngrx/store'
import { BnNgIdleService } from 'bn-ng-idle'
import { fromEvent, interval, of, Subject, Subscription, throwError } from 'rxjs'
import { debounceTime, delay, distinctUntilChanged, map, takeUntil } from 'rxjs/operators'
import { BackendService } from 'src/app/api/backend/backend.service'
import { AuthService } from 'src/app/auth.service'
import { environment } from '../environments/environment'
import { DebugContainerComponent } from './core/debug.container'
import { AppState } from './core/store'
import { setWidth, WindowSize } from './core/store/app.actions'
import * as fromAuthActions from './core/store/auth/auth.actions'
import { selectIsAuthenticated, selectIsLocktonUser } from './core/store/auth/auth.selectors'
import { NavListContainerComponent } from './nav-list.container'
import { NavService } from './nav.service'
import { PageLoadTrackingService } from './core/telemetry/page-load-tracking.service'
import { TimeoutDialogComponent } from './timeout-dialog.component'
import { UtilService } from './util.service'

import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { SvgDefsComponent } from '@shared/svg-defs.component'
import {
  ChatWindowOverlayComponent,
  ChatTool,
} from '@shared/chat-window-overlay/chat-window-overlay.component'
import { DraftActionsService } from './submit-to-market/services/draft-actions.service'
// Company-screener chat tools disabled — uncomment to re-enable the skill.
// import {
//   buildCompanyTypeChatTool,
//   buildGenerateScreenerChatTool,
//   buildScreenerTimeframeYearChatTool,
//   buildScreenerMetricThresholdChatTool,
// } from './benchmark/controls/benchmark-company-screener.chat'
// Company-screener chat tools disabled — uncomment to re-enable the skill.
// import { selectBenchmarkControlsCompanyScreenerTimeframeOptions } from './benchmark/store/benchmark-timeframe.selectors'

@Component({
  selector: 'app-root',
  styles: [
    `
      .loader-overlay {
        height: 100vh;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 10;
        top: 0;
        left: 0;
        position: fixed;

        .center {
          position: absolute;
          top: 50%;
          left: 50%;
          -moz-transform: translateX(-50%) translateY(-50%);
          -webkit-transform: translateX(-50%) translateY(-50%);
          transform: translateX(-50%) translateY(-50%);
        }
      }
      .content-all {
        display: flex;
        flex-direction: column;
        max-height: 100vh;
      }

      .content-outer-container {
        max-height: calc(100vh - var(--footer-height));
        height: calc(100vh - var(--footer-height));
      }

      .centered {
        margin-left: auto;
        margin-right: auto;
        text-align: center;
      }

      .app-title {
        margin-bottom: 0;
        margin-top: 0;
      }

      .footer-container {
        height: var(--footer-height);
        flex: 0 0 var(--footer-height);
        background: black;
        z-index: 999;
      }

      .footer-content {
        margin: 2px auto;
        text-align: center;
        color: white;
        font-size: small;
        text-align: left;
        padding-left: 10px;
      }
      .empty-container {
        height: var(--footer-height);
        flex: 0 0 var(--footer-height);
        background: white;
      }
    `,
  ],
  template: `
    <div class="content-all">
      <div class="content-outer-container">
        <mat-sidenav-container style="position: static;">
          <mat-sidenav mode="over" opened="false">
            <app-nav-list-container></app-nav-list-container>
          </mat-sidenav>
          <router-outlet></router-outlet>
        </mat-sidenav-container>
        <app-debug></app-debug>
        <app-svg-defs></app-svg-defs>
        @if (!chatHidden && isAuthenticated() && isLocktonUser()) {
          <app-chat-window-overlay
            title="SAGE Assistant"
            [tools]="chatTools"
            [additionalContext]="chatAdditionalContext"
          />
        }
        @if (spinnerVisible) {
          <div class="loader-overlay">
            <div class="center">
              <mat-progress-spinner
                mode="indeterminate"
                diameter="50"
                color="accent"
              ></mat-progress-spinner>
            </div>
          </div>
        }
      </div>
      @if (isLoading) {
        <div class="empty-container"></div>
      }
      @if (!isLoading) {
        <div class="footer-container">
          <p align="center" class="footer-content">
            <a
              class="footer-content"
              target="_blank"
              href="https://global.lockton.com/re/en/sage-patents"
              >Patent: https://global.lockton.com/re/en/sage-patents</a
            >
          </p>
        </div>
      }
    </div>
  `,
  imports: [
    MatSidenavContainer,
    MatSidenav,
    NavListContainerComponent,
    RouterOutlet,
    DebugContainerComponent,
    SvgDefsComponent,
    MatProgressSpinner,
    ChatWindowOverlayComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject<Store<AppState>>(Store)
  private nav = inject(NavService)
  private matIconRegistry = inject(MatIconRegistry)
  private domSanitizer = inject(DomSanitizer)
  private bnIdle = inject(BnNgIdleService)
  dialog = inject(MatDialog)
  private router = inject(Router)
  private backendService = inject(BackendService)
  private authService = inject(AuthService)
  private utilService = inject(UtilService)
  private pageLoadTracking = inject(PageLoadTrackingService)

  authServiceSubscription?: Subscription
  authTimerSubscription?: Subscription
  private destroy$ = new Subject()
  readonly chatHidden = environment.featureFlags['ai-chat.hidden'] ?? true
  readonly isAuthenticated = toSignal(this.store.pipe(select(selectIsAuthenticated)), {
    initialValue: false,
  })
  readonly isLocktonUser = toSignal(this.store.pipe(select(selectIsLocktonUser)), {
    initialValue: false,
  })

  private draftActionsService = inject(DraftActionsService)

  chatAdditionalContext: Record<string, string | undefined> = {}

  readonly chatTools: ChatTool[] = [
    // Benchmark tools
    // Company-screener chat tools disabled — uncomment to re-enable the skill.
    // buildCompanyTypeChatTool(this.store),
    // buildLobChatTool(this.store),
    // buildStatesChatTool(this.store),
    // buildPeriodTypeChatTool(this.store),
    // buildQuarterChatTool(this.store),
    // buildScreenerTimeframeYearChatTool(this.store),
    // buildScreenerMetricThresholdChatTool(this.store),
    // buildClearControlsChatTool(this.store),
    // buildGenerateScreenerChatTool(this.store),
    // Submit-to-market tools
    {
      name: 'rfq_refresh_data' as const,
      description:
        'Refresh the current RFQ page data from the server. Use ONLY when the user is already on the RFQ page affected by the update.',
      input_schema: { type: 'object' as const, properties: {}, required: [] },
      handler: async () => {
        const id = this.draftIdFromUrl(this.router.url)
        if (id) this.draftActionsService.getDraftData(id)
      },
    },
    {
      name: 'rfq_navigate' as const,
      description:
        'Navigate the user to a specific step within the current RFQ draft workflow in Submit-to-Market.',
      input_schema: {
        type: 'object' as const,
        required: ['path'],
        properties: {
          path: {
            type: 'string',
            enum: [
              'select-structures',
              'select-markets',
              'upload-attachments',
              'add-quote-request-data',
              'draft-dashboard',
              'email-templates',
              'send-emails',
            ],
            description: 'The RFQ wizard step to navigate to.',
          },
        },
      },
      handler: async (input: Record<string, unknown>) => {
        const id = this.draftIdFromUrl(this.router.url)
        if (id) this.router.navigate(['submit-to-market', 'draft', id, input['path'] as string])
      },
    },
  ]

  private draftIdFromUrl(url: string): number | null {
    // The id may be the last segment (`/draft/123`) or followed by a child
    // route or query string (`/draft/123/select-structures`, `/draft/123?x=y`).
    const match = url?.match(/\/draft\/(\d+)(?=[/?#]|$)/)
    return match ? parseInt(match[1], 10) : null
  }
  dialogOpen = false
  authenticated = false
  awaitingToken = false
  isLoading: boolean = true
  lastTimestamp = new Date().getTime()
  spinnerStateChanged: Subscription = new Subscription()
  public spinnerVisible: boolean = false
  @ViewChild(MatSidenav, { static: true }) sidenav?: MatSidenav

  constructor() {
    if (BrowserUtils.isInPopup()) {
      // This window is the MSAL auth popup, not the main app. msal-browser completes
      // loginPopup() by reading the hash off this window from the opener's own MSAL
      // instance. If the full app boots here too, it races/consumes that hash first
      // (e.g. via handleRedirectPromise below), so the opener's popup promise never
      // resolves and the main window spins forever.
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const action = urlParams.get('action')

    if (action === 'password-reset') {
      urlParams.set('action', 'none')
      window.history.replaceState(null, '', window.location.pathname)
      sessionStorage.clear()
      this.authService.redirectToPasswordReset()
      return
    }

    this.authServiceSubscription = this.authService.accessTokenRefreshed.subscribe((token) => {
      this.store.dispatch(fromAuthActions.updateAuthToken({ token }))
    })

    const previousUrlLastIndexOf = window.location.pathname
      .toString()
      .substr(window.location.pathname.toString().lastIndexOf('/') + 1)
    if (
      !previousUrlLastIndexOf.includes('login') &&
      !previousUrlLastIndexOf.includes('access_token') &&
      previousUrlLastIndexOf.length > 0
    ) {
      sessionStorage.setItem('reDirectUrl', window.location.pathname)
    }

    const username = sessionStorage.getItem('username')
    const isAfterLoginRedirect = window.location.hash && window.location.hash.startsWith('#state=')

    if (isAfterLoginRedirect) {
      this.authService.handleAfterLoginRedirect(username)
    } else if (username) {
      this.authService.trySignIn(username, false).then(() => {
        if (this.authService.account === null) {
          this.router.navigate(['/login'])
        }
      })
    } else {
      this.router.navigate(['/login'])
    }

    this.authTimerSubscription = interval(2000).subscribe(async () => {
      const currentTimestamp = new Date().getTime()
      if (Math.abs(currentTimestamp - this.lastTimestamp) / 360000 > environment.timeout / 3600) {
        await this.authService.signOut()
      } else {
        this.lastTimestamp = currentTimestamp
        const token = await this.authService.getAccessToken()
        if (token && this.tokenNearingExpiration(token) && !this.awaitingToken) {
          this.awaitingToken = true
          this.backendService
            .postEvent({
              eventType: 'Token Event',
              eventDetails: { type: 'Refreshing Token' },
            })
            .subscribe((err) => throwError(err))
          this.authService
            .getAccessToken(true)
            .finally(() => {
              this.awaitingToken = false
            })
            .catch((err) => {
              if (err.type !== 'silent_refresh_timeout') {
                console.error('Error refreshing token silently', err)
              }
            })
        }
      }
    })

    this.bnIdle.startWatching(environment.timeout).subscribe(async (isTimedOut: boolean) => {
      const token = await this.authService.getAccessToken()
      if (isTimedOut && !this.dialogOpen && token) {
        this.dialogOpen = true
        const dialogRef = this.dialog.open(TimeoutDialogComponent, {
          disableClose: true,
        })
        dialogRef.afterClosed().subscribe(async (result) => {
          this.dialogOpen = false
          if (result === 'Reset') {
            this.bnIdle.resetTimer()
          } else {
            await this.authService.signOut()
          }
        })
      }
    })
    // Safe: hardcoded local asset paths — no user input (SAIRF-013 reviewed)
    this.matIconRegistry.addSvgIcon(
      'word',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/word.svg'),
    )
    this.matIconRegistry.addSvgIcon(
      'excel',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/excel.svg'),
    )
    this.matIconRegistry.addSvgIcon(
      'database',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/database.svg'),
    )
    this.matIconRegistry.addSvgIcon(
      'default',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/default_file.svg'),
    )
  }

  ngOnInit(): void {
    this.pageLoadTracking.init()
    this.spinnerStateChanged = this.utilService.spinnerState.subscribe(
      (data) => (this.spinnerVisible = data),
    )

    // Benchmark context — keeps Claude informed of available options when user is in Benchmark
    /* this.store
      .pipe(takeUntil(this.destroy$), select(selectBenchmarkSubmode))
      .subscribe((submode) => {
        this.chatAdditionalContext = {
          ...this.chatAdditionalContext,
          submode: submode?.id ?? undefined,
        }
      }) */
    /* this.store
      .pipe(takeUntil(this.destroy$), select(selectBenchmarkControlsLobs))
      .subscribe((lobs) => {
        this.chatAdditionalContext = {
          ...this.chatAdditionalContext,
          availableLobs: lobs.map((l) => `id: "${l.id}" (display: "${l.name}")`).join(', '),
        }
      }) */
    // Company-screener chat tools disabled — uncomment to re-enable the skill.
    // this.store
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     select(selectBenchmarkControlsCompanyScreenerTimeframeOptions),
    //   )
    //   .subscribe((years) => {
    //     this.chatAdditionalContext = {
    //       ...this.chatAdditionalContext,
    //       availableScreenerYears: years.join(', '),
    //     }
    //   })
  }

  ngAfterViewInit(): void {
    this.nav.register(this.sidenav!)
    setTimeout(() => {
      this.isLoading = false
    }, 5000)
    // Dispatch initial window width
    of(this.getWindowSize(window))
      .pipe(
        takeUntil(this.destroy$),
        delay(0),
        map((size) => setWidth({ size })),
      )
      .subscribe(this.store)

    // Dispatch window width on resize
    fromEvent(window, 'resize')
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        distinctUntilChanged(),
        map((e) => e.target as Window),
        map((window) => this.getWindowSize(window)),
        map((size) => setWidth({ size })),
      )
      .subscribe(this.store)
  }

  ngOnDestroy(): void {
    this.authServiceSubscription?.unsubscribe()
    this.authTimerSubscription?.unsubscribe()
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  private getWindowSize(window: Window): WindowSize {
    return { width: window.innerWidth, height: window.innerHeight }
  }

  private tokenNearingExpiration(token: string) {
    const expiry = JSON.parse(atob(token.split('.')[1])).exp
    // tslint:disable-next-line: new-parens
    return Math.floor(new Date().getTime() / 1000) >= expiry - 900
  }
}
